import { Injectable } from '@angular/core';
import {
  Node,
  SearchableNode,
  NodeInTree,
  TreeTableNode
} from '../types/interfaces/tree.interface';
import * as _ from 'lodash';
import { Option, some, none, fold } from 'fp-ts/Option';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class TreeService {
  /**
   * Traverse a tree data structure and applies the provided @param f function
   * to all nodes
   * @param root the tree to be traversed
   * @param f the function to be applied to all nodes
   * N.B. this function modifies the existing tree
   */
  traverse<K extends { children: K[] }>(root: K, f: (node: K) => void): void {
    this._traverse(root, (node: K) => {
      f(node);
      return true;
    });
  }

  /**
   * Search a tree for a node with the provided @param id
   * @param root the tree to be searched
   * @param id the id of the node to be retrieved
   */
  searchById<T>(
    root: SearchableNode<T>,
    id: string | undefined
  ): Option<NodeInTree<T>> {
    // An object, not a let: TypeScript does not track assignments inside the
    // callback.
    const match: { node?: SearchableNode<T> } = {};
    const pathToRoot: { [k: string]: SearchableNode<T> } = {};
    this._traverse(root, (node: SearchableNode<T>) => {
      node.children.forEach(child => {
        if (child.id !== undefined) {
          pathToRoot[child.id] = node;
        }
      });
      if (node.id === id) {
        match.node = node;
      }
      return node.id !== id;
    });
    const matchingNode = match.node;
    return matchingNode
      ? some({
          id: matchingNode.id,
          value: matchingNode.value,
          children: matchingNode.children,
          pathToRoot: this.buildPath(id, pathToRoot)
        })
      : none;
  }

  /**
   * Internal function that can be used to traverse or search the tree
   * @param root the tree to be scanned
   * @param f an optional function to be applied to all nodes
   */
  private _traverse<K extends { children: K[] }>(
    root: K,
    f: (node: K) => boolean
  ): void {
    if (!f(root)) {
      return;
    }
    root.children.forEach(c => this._traverse(c, f));
  }

  /**
   * Given a @param root tree and a @param node node, calculate the
   * depth of the node in the tree
   * @param root the tree
   * @param node the node we want to calculate the depth of
   */
  getNodeDepth<T, K extends SearchableNode<T>>(root: K, node: K): number {
    return fold(
      () => -1,
      (n: NodeInTree<T>) => n.pathToRoot.length
    )(this.searchById(root, node.id));
  }

  /**
   * Flatten a @param root tree into a list of its nodes
   * @param root the tree to be flattened
   */
  flatten<T, K extends Node<T>>(root: K): K[] {
    const result = [_.cloneDeep(root)];
    for (let i = 0; i < result.length; i++) {
      const node = result[i];
      if (node.children) {
        result.splice(result.indexOf(node) + 1, 0, ...(node.children as K[]));
      }
    }
    return result;
  }

  /**
   * Internal function used to build the pathToRoot of a node in a tree
   * @param id the id of the node
   * @param pathMap the pathMap returned by searchById
   */
  private buildPath<T>(
    id: string | undefined,
    pathMap: { [k: string]: SearchableNode<T> }
  ): SearchableNode<T>[] {
    const pathToRoot: SearchableNode<T>[] = [];
    let key: string | null | undefined = id;
    while (key) {
      if (pathMap[key]) {
        pathToRoot.push(pathMap[key]);
        key = pathMap[key].id;
      } else {
        key = null;
      }
    }
    return pathToRoot;
  }

  /**
   * Clone a Node<T> object and convert it to a SearchableNode<T>
   * @param tree the node to be converted
   */
  toSearchableTree<T>(tree: Node<T>): SearchableNode<T> {
    const treeClone = _.cloneDeep(tree) as SearchableNode<T>;
    this.traverse(treeClone, (node: SearchableNode<T>) => {
      node.id = node.id ? node.id : uuidv4();
    });
    return treeClone;
  }

  /**
   * Clone a SearchableNode<T> object and convert it to a TreeTableNode<T>
   * @param tree the node to be converted
   */
  toTreeTableTree<T>(tree: SearchableNode<T>): TreeTableNode<T> {
    const treeClone = _.cloneDeep(tree) as TreeTableNode<T>;
    this.traverse(treeClone, (node: TreeTableNode<T>) => {
      node.depth = this.getNodeDepth(treeClone, node);
      node.expanded = false;
      node.visible = false;
    });
    return treeClone;
  }
}
